import React from 'react';
import { array, bool, func, number, object, string } from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { I18nProvider } from '@fiverr-private/fiverr_context';
import FakeCheckbox from '../FakeCheckbox';
import FormGigItem from '../FormGigItem';
import { getPackageById, getSingleCalculator } from '../../../reducers/packages';
import { updateCustomExtra, updateExtraFast, updateFeature, updateVoData } from '../../../actions/packages';
import {
    BASIC_PACKAGE_ID,
    CUSTOM_EXTRA_TYPE,
    EXTRA_FAST_TYPE,
    FEATURE_TYPE
} from '../../../utils/packagesUtils/constants';
import { getFixedDropdownArr, getValidExtras, isVoiceOver } from '../../../utils/packagesUtils';
import calculateVoData from '../../../utils/voiceOver';

import './style.scss';
import { getBuyerTooltip } from '../../../utils/translation/pricingFactors/buyerTooltip';
import { getLabel } from '../../../utils/translation/pricingFactors/label';

class PackageExtra extends React.Component {

    constructor(props) {
        super(props);

        this.onCheckboxChange = this.onCheckboxChange.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);
        this.formatSelectorOption = this.formatSelectorOption.bind(this);
    }

    onCheckboxChange(checked) {
        this.onChange(checked ? 1 : 0);
    }

    onSelectChange({ target }) {
        const { value } = target,
            numberedValue = Number(value);

        this.onChange(numberedValue);
    }

    onChange(amount) {
        const { type, id, packageId, onExtraChange, updateVoData } = this.props;
        const { general } = this.context;

        onExtraChange({
            type,
            id,
            packageId,
            amount
        });

        if (isVoiceOver(general)) {
            updateVoData({ packageId });
        }
    }

    isChecked() {
        const { amount } = this.props;

        return amount >= 1;
    }

    formatSelectorOption(val) {
        const { currentPrice } = this.props;
        const { currencyConverter } = this.context;

        return `${val} (${currencyConverter.convert(val * currentPrice)})`;
    }

    calculateLabel(i18n) {
        const { name, label } = this.props;
        const { subCategoryId } = this.context.general;

        return this.shouldTranslateExtra()
            ? getLabel({ i18n, name, subCategoryId })
            : label;
    }

    calculateBuyerTooltip(i18n) {
        const { name, buyerTooltip } = this.props;
        const { subCategoryId } = this.context.general;

        return this.shouldTranslateExtra()
            ? getBuyerTooltip({ i18n, name, subCategoryId })
            : buyerTooltip;
    }

    shouldTranslateExtra() {
        const { type } = this.props;

        return type === FEATURE_TYPE;
    }

    render() {
        const {
            label,
            currentPrice,
            amount,
            id,
            currentDuration,
            hasQuantity
        } = this.props;

        const { currencyConverter } = this.context;

        if (!currentPrice || !label) {
            return null;
        }

        const checked = this.isChecked();

        return (
            <li className={classNames({ checked })}>
                <FormGigItem id={id} quantity={amount || 0}/>
                {

                    // This div is needed because in the single package component we have different
                    // padding for it and since we're using a transition on max-height we don't want
                    // the padding to be part of the transition
                }
                <div className="package-extra">
                    <I18nProvider>
                        {({ i18n }) => (<>
                            <FakeCheckbox checked={checked}
                                label={this.calculateLabel(i18n)}
                                tooltip={this.calculateBuyerTooltip(i18n)}
                                duration={currentDuration}
                                onChange={this.onCheckboxChange}/>
                            {
                                hasQuantity ?
                                    <select value={amount || 1} onChange={this.onSelectChange}>
                                        {
                                            getFixedDropdownArr().map((val) => (
                                                <option key={val} value={val}>
                                                    {this.formatSelectorOption(val)}
                                                </option>)
                                            )
                                        }
                                    </select> :
                                    <p>{currencyConverter.convert(currentPrice)}</p>
                            }
                        </>)}
                    </I18nProvider>
                </div>
            </li>
        );
    }
}

PackageExtra.propTypes = {
    id: number,
    name: string,
    label: string,
    buyerTooltip: string,
    currentPrice: number,
    amount: number,
    onExtraChange: func,
    updateVoData: func,
    currentDuration: number,
    hasQuantity: bool,
    type: string,
    packageId: number
};

PackageExtra.defaultProps = {
    onExtraChange: _.noop,
    updateVoData: _.noop,
    hasQuantity: true
};

PackageExtra.contextTypes = {
    currencyConverter: object,
    general: object,
    logger: object
};

const mapStateToPackageExtraProps = (state, ownProps) => ({
    label: ownProps.label,
    buyerTooltip: ownProps.buyerTooltip,
    currentPrice: ownProps.currentPrice,
    amount: ownProps.amount,
    currentDuration: ownProps.currentDuration,
    packageId: ownProps.packageId
});

const onExtraChange = ({ type, id, packageId, amount }) => (dispatch) => {
    switch (type) {
        case FEATURE_TYPE:
            dispatch(updateFeature({ featureId: id, amount }));
            break;
        case CUSTOM_EXTRA_TYPE:
            dispatch(updateCustomExtra({ customExtraId: id, amount }));
            break;
        case EXTRA_FAST_TYPE:
            dispatch(updateExtraFast({ value: amount, packageId }));
            break;
        default:
            break;
    }
};

const dispatchUpdateVoData = ({ packageId }) => (dispatch, getState) => {
    const { packages } = getState();
    const packageData = getPackageById(packages, packageId);
    const calculator = getSingleCalculator(packages, packageId);
    if (!calculator) {
        return;
    }
    const { id, currentValue } = calculator;
    const voData = calculateVoData({ scriptLength: currentValue, calculatorId: id, packageData });
    dispatch(updateVoData({ voData }));
};

const ConnectedPackageExtra = connect(
    mapStateToPackageExtraProps,
    {
        onExtraChange,
        updateVoData: dispatchUpdateVoData
    }
)(PackageExtra);

const PackageExtras = ({ features, customExtras, extraFast, id }) => (
    <I18nProvider>
        {({ i18n }) => {
            const extras = getValidExtras({ features, customExtras, extraFast, i18n });

            if (!extras.length) {
                return null;
            }

            return (
                <ul className="package-extras">
                    {extras.map((extra, index) => (
                        <ConnectedPackageExtra key={index} {...extra} packageId={id}/>
                    ))}
                </ul>
            );
        }}
    </I18nProvider>
);

PackageExtras.propTypes = {
    features: array,
    customExtras: array,
    extraFast: object,
    id: number
};

PackageExtras.defaultProps = {
    features: [],
    customExtras: []
};

const mapStateToProps = ({ packages }) => {
    const packageData = getPackageById(packages, BASIC_PACKAGE_ID);

    return {
        ...packageData
    };
};

export { PackageExtras, PackageExtra };
export default connect(mapStateToProps)(PackageExtras);
